import React, { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  diagnosticoPush,
  seguirPedido,
  dejarDeSeguirPedido,
  sigoPedido,
  pedidoAdmiteAvisos,
} from '../../utils/pushSubscription';

/**
 * Botón "Avisame cuando cambie mi pedido" para el cliente de la tienda.
 * Lo usa la pantalla de seguimiento (/rastreo/:code), que es también la
 * confirmación del checkout (?new=true).
 *
 * - Pedidos cerrados (entregado/cancelado): no se muestra.
 * - iPhone/iPad sin la app instalada: iOS no permite push → se ofrece WhatsApp.
 * - Navegador sin soporte: se ofrece WhatsApp si la tienda lo tiene.
 */
export default function PushAvisoPedido({ trackingCode, status, whatsappLink, destacado = false }) {
  const [siguiendo, setSiguiendo] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [diag] = useState(() => diagnosticoPush());
  // null = consultando. Pedidos de Tienda Nube o cerrados no admiten avisos.
  const [admite, setAdmite] = useState(null);

  const cerrado = ['entregado', 'cancelado'].includes(String(status || '').toLowerCase());

  useEffect(() => {
    let vivo = true;
    if (!trackingCode) return undefined;
    pedidoAdmiteAvisos(trackingCode).then((v) => { if (vivo) setAdmite(v); });
    return () => { vivo = false; };
  }, [trackingCode, status]);

  useEffect(() => {
    let vivo = true;
    if (!trackingCode || diag.estado !== 'disponible') return undefined;
    sigoPedido(trackingCode).then((v) => { if (vivo) setSiguiendo(v); }).catch(() => {});
    return () => { vivo = false; };
  }, [trackingCode, diag.estado]);

  if (!trackingCode || cerrado || admite !== true) return null;

  const contenedor = `bg-white rounded-2xl shadow-sm border p-5 flex items-center gap-4 ${destacado && !siguiendo ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-slate-200'}`;

  if (diag.estado === 'requiere_instalar' || diag.estado === 'no_soportado') {
    if (!whatsappLink) return null;
    return (
      <div className={contenedor}>
        <div className="w-11 h-11 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0">
          <MessageCircle size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-slate-800">¿Querés saber cuándo está listo?</p>
          <p className="text-xs text-slate-500 mt-0.5">Consultanos por WhatsApp y te avisamos.</p>
        </div>
        <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
          className="px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-xs font-black uppercase tracking-wider shrink-0">
          WhatsApp
        </a>
      </div>
    );
  }

  if (diag.estado === 'bloqueado') {
    return (
      <div className={contenedor}>
        <div className="w-11 h-11 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 shrink-0">
          <BellOff size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-slate-800">Avisos bloqueados</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Para recibir avisos de tu pedido, habilitá las notificaciones de este sitio desde el candado de la barra de direcciones.
          </p>
        </div>
      </div>
    );
  }

  const activar = async () => {
    setCargando(true);
    try {
      await seguirPedido(trackingCode);
      setSiguiendo(true);
      toast.success('Listo: te avisamos cada vez que cambie tu pedido.');
    } catch (e) {
      toast.error(e.message || 'No se pudieron activar los avisos.');
    } finally {
      setCargando(false);
    }
  };

  const desactivar = async () => {
    setCargando(true);
    try {
      await dejarDeSeguirPedido(trackingCode);
      setSiguiendo(false);
      toast.success('Ya no vas a recibir avisos de este pedido.');
    } catch {
      toast.error('No se pudieron desactivar los avisos.');
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className={contenedor}>
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${siguiendo ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-600'}`}>
        {siguiendo ? <BellRing size={20} /> : <Bell size={20} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-black text-slate-800">
          {siguiendo ? 'Te avisamos los cambios' : '¿Te avisamos cuando cambie tu pedido?'}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          {siguiendo
            ? 'Vas a recibir una notificación en este dispositivo en cada cambio de estado.'
            : 'Recibí una notificación cuando esté en preparación, listo o en camino.'}
        </p>
      </div>
      {siguiendo ? (
        <button type="button" onClick={desactivar} disabled={cargando}
          className="px-3 py-2 rounded-xl border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-wider shrink-0 disabled:opacity-50">
          Desactivar
        </button>
      ) : (
        <button type="button" onClick={activar} disabled={cargando}
          className="px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-black uppercase tracking-wider shrink-0 disabled:opacity-50">
          {cargando ? 'Activando…' : 'Avisame'}
        </button>
      )}
    </div>
  );
}
